import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { ApiResponse } from '../../../../types/admin';

interface ResponseViewerProps {
  response: ApiResponse | null;
  error: string | null;
  loading: boolean;
}

export const ResponseViewer: React.FC<ResponseViewerProps> = ({
  response,
  error,
  loading,
}) => {
  if (error) {
    return (
      <Alert severity="error">
        <Typography variant="h6">Error</Typography>
        <Typography>{error}</Typography>
      </Alert>
    );
  }

  if (!response && !loading) {
    return (
      <Card>
        <CardContent>
          <Typography color="text.secondary">
            Select an endpoint above and it will be executed automatically.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return 'success';
    if (status >= 400 && status < 500) return 'warning';
    if (status >= 500) return 'error';
    return 'default';
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h6">Response</Typography>
          {response && (
            <>
              <Chip
                label={`${response.status} ${response.statusText}`}
                color={getStatusColor(response.status)}
                size="small"
              />
              <Chip
                label={`${response.executionTime}ms`}
                variant="outlined"
                size="small"
              />
            </>
          )}
          {loading && (
            <Chip
              label="Loading..."
              color="info"
              size="small"
            />
          )}
        </Box>

        {response && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Executed at: {new Date(response.timestamp).toLocaleString()}
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body1">Response Headers</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography color="text.secondary">Loading headers...</Typography>
              </Box>
            ) : response ? (
              <Box
                component="pre"
                sx={{
                  backgroundColor: '#f5f5f5',
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  fontSize: '0.75rem',
                  fontFamily: 'monospace',
                  maxHeight: 200,
                }}
              >
                {JSON.stringify(response.headers, null, 2)}
              </Box>
            ) : (
              <Typography color="text.secondary">No headers available</Typography>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body1">Response Body</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6 }}>
                <CircularProgress size={32} sx={{ mr: 2 }} />
                <Typography color="text.secondary">Executing API request...</Typography>
              </Box>
            ) : response ? (
              <Box
                component="pre"
                sx={{
                  backgroundColor: '#f5f5f5',
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  fontSize: '0.75rem',
                  fontFamily: 'monospace',
                  maxHeight: 800,
                }}
              >
                {JSON.stringify(response.data, null, 2)}
              </Box>
            ) : (
              <Typography color="text.secondary">No response data available</Typography>
            )}
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  );
};